import { type SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { Effect } from "effect";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
  createTypingPresenceScheduler,
  type TypingPresenceScheduler,
} from "./typing-presence-scheduler";

/**
 * Notifies the server that the current user is viewing a support ticket.
 * Sends presence.viewing on mount and presence.cleared on unmount.
 */
export function usePresenceViewing(supportTicketId: SupportTicketId): void {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const auth = useContext(AuthContext);

  useEffect(() => {
    if (!auth?.userId) {
      return;
    }

    const userId = auth.userId;
    const client = effectApi.getClient();

    void effectApi.runPromise(
      client["team-notification"]
        .notifyPresence({
          payload: { event: "presence.viewing", supportTicketId, userId },
        })
        .pipe(Effect.orDie),
    );

    return () => {
      void effectApi.runPromise(
        client["team-notification"]
          .notifyPresence({
            payload: { event: "presence.cleared", userId, supportTicketId },
          })
          .pipe(Effect.orDie),
      );
    };
  }, [supportTicketId, effectApi, auth?.userId]);
}

/**
 * Returns a stable function that notifies the server the current user is typing.
 * Typing RPCs are throttled to one per refresh interval. The backend handles
 * expiry automatically via Redis TTL — no client-side clear is needed.
 */
export function useNotifyTyping(supportTicketId: SupportTicketId): () => void {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const auth = useContext(AuthContext);
  const schedulerRef = useRef<TypingPresenceScheduler | null>(null);

  const sendTyping = useCallback(() => {
    if (!auth?.userId) {
      return;
    }

    const userId = auth.userId;
    const client = effectApi.getClient();

    void effectApi.runPromise(
      client["team-notification"]
        .notifyPresence({
          payload: { event: "presence.typing", supportTicketId, userId },
        })
        .pipe(Effect.orDie),
    );
  }, [supportTicketId, effectApi, auth?.userId]);

  useEffect(() => {
    schedulerRef.current = createTypingPresenceScheduler({ sendTyping });

    return () => {
      schedulerRef.current?.dispose();
      schedulerRef.current = null;
    };
  }, [sendTyping]);

  return useCallback(() => {
    if (!schedulerRef.current) {
      return;
    }

    schedulerRef.current.recordKeystroke();
  }, []);
}
